import React, { useCallback, useEffect, useLayoutEffect, useMemo, useState } from 'react';
import { Route, Switch, useHistory, useParams, useRouteMatch } from 'react-router-dom';
import TabsLinks from '../../../../components/TabsLinks';
import Layout from '../../../../components/Layout';
import { paths } from '../../../../routes';
import { StyledJobs, StyledJobsContent } from './styled';
import AppBar from '../../../../components/AppBar';
import { useApi } from '../../../../hooks/useApi';
import Loader from '../../../../components/Loader';
import { useManage } from '../../../../hooks/useManage';
import { OPERATIONS } from '../../../../utils/constans';
import Button from '../../../../components/Button';
import Icon from '../../../../components/Icon';
import FormJob from '../../../../forms/FormJob';
import Modal from '../../../../components/Modal';
import { ApiJobs } from '../../../../api/jobs';
import JobsActive from './pages/JobsActive';
import JobsCompleted from './pages/JobsCompleted';
import { ApiProfessions } from '../../../../api/professions';
import { useDispatch, useSelector } from 'react-redux';
import PlaceSelect from '../../components/PlaceSelect';
import FormJobDelete from '../../../../forms/FormJobDelete';
import { findByID, parseJobs } from '../../../../utils/helpers';
import { toast } from 'react-toastify';
import { Col, Row } from 'react-styled-flexboxgrid';
import Search from '../../../../components/Search';
import { setIsReloadJobs } from '../../../../redux/notifications/actions';
import { useTranslation } from 'react-i18next';
import {makeStyles} from "../../../../utils/styles"
import DatePicker from '../../../../components/DatePicker';
import Box from '../../../../components/Box';
import IconWrap from '../../../../components/IconWrap';
import useTheme from '../../../../hooks/useTheme';
import { endOfDay, subDays } from "date-fns";


const useStyles = makeStyles((css) => ({
    datePickers: css`
        display: flex;
        align-items: center;
    `,
    datePickersSeparator: css`
        margin: 0 12px 0px 12px;
    `,
}));

const Jobs = () => {
    const { t } = useTranslation()
    const dispatch = useDispatch();
    const matchJobsActive = useRouteMatch(paths.jobsActive);
    const matchJobsCompleted = useRouteMatch(paths.jobsCompleted);
    const history = useHistory();
    const { id } = useParams();
    const [{ operation, id: activeID }, setState] = useManage();

    const [searchShow, setSearchShow] = useState(false);
    const [searchText, setSearchText] = useState("");
    const [startDate, setStartDate] = useState(subDays(new Date(), 31));
    const [endDate, setEndDate] = useState(endOfDay(new Date()));

    const styles = useStyles();
    const theme = useTheme();

    // Setup request
    const [{ data: professions, loading: professionsLoading }] = useApi(ApiProfessions.get);
    const [{ data: jobsActive, loading: jobsActiveLoading }, getActiveJobs] = useApi(ApiJobs.getActiveJobs, { manual: true });
    const [{ data: jobsCompleted, loading: jobsCompletedLoading }, getCompletedJobs] = useApi(ApiJobs.getCompletedJobs, { manual: true });
    const [{ loading: createJobLoading }, createJob] = useApi(ApiJobs.create, { manual: true });
    const [{ loading: updateJobLoading }, updateJob] = useApi(ApiJobs.update, { manual: true });
    const [{ loading: deleteJobLoading }, deleteJob] = useApi(ApiJobs.delete, { manual: true });

    const role = useSelector(state => state.auth.user?.user?.role?.type);
    const placesLoading = useSelector(state => state.places.loading);
    const placeSelected = useSelector(state => state.places.selected);
    const placesData = useSelector(state => state.places.data);
    const isReloadJobs = useSelector(state => state.notifications?.isReloadJobs);

    // Memoize data
    const jobsActiveParsed = useMemo(
        () => parseJobs(jobsActive),
        [jobsActive]
    );

    const jobsCompletedParsed = useMemo(
        () => parseJobs(jobsCompleted),
        [jobsCompleted]
    );

    const job = useMemo(
        () => {
            if (matchJobsActive) {
                return findByID(jobsActiveParsed, activeID);
            } else {
                return findByID(jobsCompletedParsed, activeID);
            }
        },
        [matchJobsActive, jobsActiveParsed, jobsCompletedParsed, activeID]
    );

    const getActiveJobsByPlace = useCallback(
        () => {
            if (placeSelected?.id) {
                const placeId = placeSelected.id === 'all' ? null : placeSelected.id;
                return getActiveJobs({ place_id: placeId });
            }
        },
        [placeSelected, getActiveJobs],
    );

    const getCompletedJobsByPlace = useCallback(
        () => {
            if (placeSelected?.id) {
                const placeId = placeSelected.id === 'all' ? null : placeSelected.id;
                return getCompletedJobs({ place_id: placeId });
            }
        },
        [placeSelected, getCompletedJobs],
    );

    // Redirect to job from url id
    useLayoutEffect(() => {
        if (!matchJobsActive && !matchJobsCompleted) {
            if (id) {
                const redirectToActive = !!findByID(jobsActiveParsed, id);
                if (redirectToActive) {
                    history.replace(`${paths.jobsActive}/${id}`);
                } else {
                    const redirectToCompleted = !!findByID(jobsCompletedParsed, id);
                    if (redirectToCompleted) {
                        history.replace(`${paths.jobsCompleted}/${id}`);
                    }
                }
            }
        }
    }, [id, matchJobsActive, matchJobsCompleted, jobsActiveParsed, jobsCompletedParsed, history])

    useEffect(() => {
        getActiveJobsByPlace();
        getCompletedJobsByPlace();
    }, [getActiveJobsByPlace, getCompletedJobsByPlace]);

    useEffect(() => {
        if (isReloadJobs) {
            setTimeout(async () => {
                await getActiveJobsByPlace();
                dispatch(setIsReloadJobs(false));
            }, 2000);
        }
    }, [isReloadJobs, dispatch, getActiveJobsByPlace, getCompletedJobsByPlace]);

    const handleChangeSearch = e => setSearchText(e.target.value);

    const handleChangeStartDate = value => {
        setStartDate(value)
    };
    const handleChangeEndDate = value => setEndDate(value);

    const handleClickSearchIcon = () => setSearchShow(prevState => !prevState);

    const handleClickAdd = () => setState(OPERATIONS.ADD);

    const handleClickEdit = id => setState(OPERATIONS.EDIT, id);

    const handleClickDuplicate = id => setState(OPERATIONS.DUPLICATE, id);

    const handleClickDelete = id => setState(OPERATIONS.DELETE, id);

    const handleClickCancelModal = () => setState(OPERATIONS.READ);

    const handleSubmit = async data => {
        if (operation === OPERATIONS.EDIT) {
            //UPDATE
            const res = await updateJob(activeID, data);
            if (res?.data) {
                toast.success(t('Zlecenie zostało zaktualizowane'));
                setState(OPERATIONS.READ, null);
                getActiveJobsByPlace();
            }
        } else {
            //CREATE
            const res = await createJob(data);
            if (res?.data) {
                toast.success(t('Zlecenie zostało dodane'));
                setState(OPERATIONS.READ, null);
                getActiveJobsByPlace();
            }
        }
    };

    const handleSubmitDelete = async data => {
        //DELETE
        const res = await deleteJob(activeID, data);
        if (res?.data) {
            toast.success(t('Zlecenie zostało usunięte'));
            setState(OPERATIONS.READ, null);
            getActiveJobsByPlace();
        }
    };

    return (
        <StyledJobs>
            <AppBar>
                <PlaceSelect />
            </AppBar>
            <TabsLinks small
                tabs={[{
                    to: paths.jobsActive,
                    name: t('Zlecenia aktywne')
                }, {
                    to: paths.jobsCompleted,
                    name: t('Zlecenia zakończone')
                }]}
                
                rightContent={
                    <Row middle="xs">
                       
                        <Col>
                            <Search
                                value={searchText}
                                onChange={handleChangeSearch}
                                expanded={searchShow}
                                onClickSearchIcon={handleClickSearchIcon}
                                small
                            />
                        </Col>
                        <Col>
                            <Box customStyle={styles.datePickers}>
                                <DatePicker
                                    selected={startDate}
                                    onChange={handleChangeStartDate}
                                    selectsStart
                                    startDate={startDate}
                                    endDate={endDate}
                                    small
                                    woIcon
                                />
                                <IconWrap customStyle={styles.datePickersSeparator}>
                                    <Icon name="minus" width={8} height={8} fill={theme.colors.darkGrey} />
                                </IconWrap>
                                <DatePicker
                                    selected={endDate}
                                    onChange={handleChangeEndDate}
                                    selectsEnd
                                    startDate={startDate}
                                    endDate={endDate}
                                    minDate={startDate}
                                    small
                                    woIcon
                                />
                            </Box>
                        </Col>
                        <Col>
                            <Button
                                startIcon={
                                    <Icon
                                        name="add"
                                        width={12}
                                        height={12}
                                    />
                                }
                                onClick={handleClickAdd}
                            >
                                {t("DODAJ ZLECENIE")}
                            </Button>
                        </Col>
                    </Row>
                }
            />
            <Layout>
                <StyledJobsContent>
                    <Switch>
                        <Route
                            exact
                            path={[paths.jobsActive, `${paths.jobsActive}/:id`]}
                        >
                            <JobsActive
                                jobs={jobsActiveParsed}
                                searchText={searchText}
                                startDate={startDate}
                                endDate={endDate}
                                refetchJobsActive={getActiveJobsByPlace}
                                onClickEdit={handleClickEdit}
                                onClickDuplicate={handleClickDuplicate}
                                onClickDelete={handleClickDelete}
                            />
                        </Route>
                        <Route
                            exact
                            path={[paths.jobsCompleted, `${paths.jobsCompleted}/:id`]}
                        >
                            <JobsCompleted
                                jobs={jobsCompletedParsed}
                                searchText={searchText}
                                startDate={startDate}
                                endDate={endDate}
                                refetchJobsCompleted={getCompletedJobsByPlace}
                                onClickDuplicate={handleClickDuplicate}
                            />
                        </Route>
                    </Switch>
                </StyledJobsContent>
            </Layout>
            <Modal
                open={operation === OPERATIONS.ADD || operation === OPERATIONS.EDIT || operation === OPERATIONS.DUPLICATE}
            >
                <FormJob
                    role={role}
                    loading={professionsLoading || createJobLoading || updateJobLoading}
                    edit={operation === OPERATIONS.EDIT}
                    initialValues={operation === OPERATIONS.EDIT || operation === OPERATIONS.DUPLICATE ? job?.form_values : placeSelected?.id !== 'all' ? { place_id: placeSelected?.id } : {}}
                    professions={professions}
                    suppliers={placeSelected?.user_suppliers ?? []}
                    places={placesData}
                    onSubmit={handleSubmit}
                    onClickCancel={handleClickCancelModal}
                />
            </Modal>
            <Modal
                open={operation === OPERATIONS.DELETE}
            >
                <FormJobDelete
                    loading={deleteJobLoading}
                    job={job}
                    onSubmit={handleSubmitDelete}
                    onClickCancel={handleClickCancelModal}
                />
            </Modal>
            <Loader show={(!isReloadJobs && jobsActiveLoading) || jobsCompletedLoading || placesLoading || professionsLoading || createJobLoading || updateJobLoading || deleteJobLoading} />
        </StyledJobs>
    )
}

export default Jobs;
